/**
 * Composition pour les pages d'authentification
 */

import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
/***/
import { useHistoryStore } from '@stores/history';
import { useAuthUserStore } from '@stores/auth';
/***/
import UserModel from '@models/UserModel';

/**
 * Méthode pour l'initialisation des pages d'authentification
 * L'utilisateur est automatiquement redirigé s'il est authentifié
 * @return object
 */
export function useInitAuthPage({}) {

    const historyStore = useHistoryStore();
    const authUserStore = useAuthUserStore();

    const previous = computed(() => historyStore.getPrevious);

    const authenticated = computed(() => authUserStore.authenticated);

    const router = useRouter();

    const authPreparing = ref(true);

    const redirectIfLogged = (() => {
        if(! authenticated.value) {
            authPreparing.value = false;
            return;
        }

        let redirectRoute = {
            name: 'home'
        };

        if(previous.value !== null) {
            redirectRoute = previous.value;
        }

        router.push(redirectRoute);
    });

    // Redirige l'utilisateur s'il est déjà connecté
    onMounted(() => {
        redirectIfLogged();
    });

    return {
        authPreparing,
        redirectIfLogged
    };
};

/**
 * Méthode pour récupérer l'utilisateur connecté et vérifier ses permissions
 * @return object
 */
export function useAuthUser({}) {

    const authUserStore = useAuthUserStore();

    const authUser = computed(() => authUserStore.getUser);

    // Modèle de l'utilisateur connecté
    const authUserModel = computed(() => new UserModel(authUser.value));

    // Retourne si l'utilisateur connecté a la permission en paramètre
    const authUserHasPermission = ((permission) => {
         return authUserStore.hasPermission(permission);
    });

    // Retourne si l'utilisateur connecté est un Super Admin
    const authUserIsSuperAdmin = computed(() => authUserStore.isSuperAdmin);

    // Vrai si l'utilisateur connecté est un manager d'agence
    const authUserIsAgencyManager = computed(() => (authUserModel.value?.isAgencyManager() ?? false));

    // Retourne si l'utilisateur connecté est un manager
    const authUserIsTeamManager = computed(() => {
        if(! authUser.value) {
            return false;
        }
        return authUserModel.value.isTeamManager();
    });

    // Retourne si l'utilisateur connecté est un agent
    const authUserIsAgent = computed(() => {
        if(! authUser.value) {
            return false;
        }
        return authUserModel.value.isAgent();
    });

    // Retourne si l'utilisateur connecté est un client
    const authUserIsCustomer = computed(() => {
        if(! authUser.value) {
            return false;
        }
        return authUserModel.value.isCustomer();
    });

    // Retourne la société de l'utilisateur connecté
    const authUserSociety = computed(() => {
        if(! authUser.value) {
            return null;
        }
        return authUser.value.society;
    });

    // Retourne le logo de la société
    const authUserSocietyLogo = computed(() =>  {
        const society = authUserSociety.value;
        if(society === null) {
            return null;
        }
        const logoVersions = (society.logo ?? {});
        const logo = (logoVersions.large ?? null);
        return ((logo !== null) ? logoVersions : null);
    });

    // Retourne si l'utilisateur a un compte actif
    const hasEnabledSubscription = computed(() => {
        const subscription = authUserStore.getCreditSubscription;
        return (subscription !== null && subscription.status === 'ENABLED');
    });

    // Retourne si on connait les réseaux de l'utilisateur : soit des réseaux déjà lié, soit sélectionné pour être utilisé avec l'IA
    const hasNetworks = computed(() => {
        if(! authUser.value) {
            return false;
        }

        return (userNetworks.value.length > 0);
    });

    // Retourne les réseaux de l'utilisateur (FACEBOOK, INSTAGRAM, LINKEDIN)
    const userNetworks = computed(() => {
        if(! authUser.value) {
            return [];
        }

        const socialIdentities = (authUser.value.socialNetworkIdentities ?? []).filter((identity) => identity.enabled);
        if(socialIdentities.length > 0) {
            return socialIdentities.map((identity) => identity.network);
        }

        const aiNetworks = ((authUser.value.settings && authUser.value.settings.aiNetworks) ?? {});
        return (Object.keys(aiNetworks).filter((networkName) => aiNetworks[networkName].enabled));
    })

    // Retourne si l'utilisateur est autorisé à effectuer des actions sans abonnement
    const hasFreeSubscription = (() => {
        if(! authUser.value || ! authUser.value.freeSubscriptionUntil) {
            return false;
        }

        const currentDate = new Date();
        const freeUntil = new Date(authUser.value.freeSubscriptionUntil);
        return (freeUntil >= currentDate);
    });

    // Retourne l'URL de l'image de profil de l'utilisateur connecté
    const getAuthUserProfileUrl = ((version) => {
        if(authUser.value && authUser.value.profileImage) {
            return authUser.value.profileImage[version].url;
        }
        return '/images/empty/medium.webp';
    });

    return {
        authUserIsSuperAdmin,
        authUserIsAgencyManager,
        authUserIsTeamManager,
        authUserIsAgent,
        authUserIsCustomer,
        authUser,
        authUserHasPermission,
        authUserSociety,
        authUserSocietyLogo,
        hasEnabledSubscription,
        hasNetworks,
        userNetworks,
        hasFreeSubscription,
        getAuthUserProfileUrl
    };
};
